import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "fullWidth", "mobileBreakpoint", "mobileBreakpointContainerRef", "mobileDropdownProps", "onTabChange", "selectedTabKey", "size", "tabs", "type"],
  _excluded2 = ["direction", "disabled"];
import React, { useCallback, useMemo, useRef, createElement as _createElement } from 'react';
import { useTheme } from '@mui/material/styles';
import { useConvertToMobile, useElementDimensions } from '../../utils';
import { TabButton } from '../TabButton';
import { InputDropdown } from '../../InputDropdown';
import { Button } from '../../Button';
import Box from '@mui/material/Box';
import MuiTabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import { Icon } from '../../Icon';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var spacingMap = {
  buttonGray: '8px',
  buttonPrimary: '8px',
  buttonPrimaryBorder: '8px',
  buttonWhite: '8px',
  buttonWhiteBorder: '8px',
  underline: '16px',
  underlineFilled: '16px'
};
var bwBorderRadius = '8px';
var bwPadding = '4px';

/**
 * Component that makes it easy for users to switch between different views.
 * To display data for each tab, create TabPanel component for each tab and pass
 * them as children of HorizontalTabs
 * (see https://uiassets.ingrammicro.com/ui-libv2/index.html?path=/story/components-tabs-horizontaltabs--dynamic-tabs-example).
 */
export function HorizontalTabs(props) {
  var _selectedTab$tab, _underlineBorder$bord;
  var children = props.children,
    _props$fullWidth = props.fullWidth,
    fullWidth = _props$fullWidth === void 0 ? false : _props$fullWidth,
    mobileBreakpoint = props.mobileBreakpoint,
    mobileBreakpointContainerRef = props.mobileBreakpointContainerRef,
    mobileDropdownProps = props.mobileDropdownProps,
    onTabChange = props.onTabChange,
    selectedTabKey = props.selectedTabKey,
    _props$size = props.size,
    size = _props$size === void 0 ? 'sm' : _props$size,
    tabs = props.tabs,
    _props$type = props.type,
    type = _props$type === void 0 ? 'buttonPrimary' : _props$type,
    otherTabsProps = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var isMobile = useConvertToMobile(mobileBreakpoint, mobileBreakpointContainerRef);
  var tabsRef = useRef(null);
  var _useElementDimensions = useElementDimensions(tabsRef),
    tabsHeight = _useElementDimensions.height;
  var handleTabChange = useCallback(function (e, newKey) {
    onTabChange(e, newKey);
  }, [onTabChange]);
  var tabButtons = useMemo(function () {
    return tabs.map(function (tab, i) {
      return /*#__PURE__*/_createElement(TabButton, _extends({}, tab, {
        badge: tab.badge,
        badgeLabel: tab.badgeLabel,
        current: selectedTabKey === tab.key,
        fullWidth: fullWidth,
        key: "".concat(tab.key, "-tab-button"),
        label: tab.label,
        onClick: function onClick(e) {
          return handleTabChange(e, tab.key);
        },
        size: size,
        sx: {
          mr: i < tabs.length - 1 ? spacingMap[type] : 0
        },
        type: type
      }));
    });
  }, [fullWidth, handleTabChange, selectedTabKey, size, tabs, type]);
  var dropdownItems = useMemo(function () {
    return tabs.map(function (tab) {
      return _extends({
        disabled: (tab == null ? void 0 : tab.disabled) || false,
        label: tab.label,
        selected: selectedTabKey === tab.key,
        value: tab.key
      }, (tab == null ? void 0 : tab.badgeLabel) && {
        secondaryText: tab == null ? void 0 : tab.badgeLabel
      });
    });
  }, [selectedTabKey, tabs]);
  var selectedTab = useMemo(function () {
    var selectedTabIndex = 0;
    var tabButton = tabs.find(function (tab, i) {
      selectedTabIndex = i;
      return tab.key === selectedTabKey;
    });
    return {
      index: tabButton && selectedTabIndex,
      tab: tabButton
    };
  }, [selectedTabKey, tabs]);
  var bwBorder = "1px solid ".concat(theme.palette.gray[100]);
  var underlineBorder = type === 'underline' || type === 'underlineFilled' ? {
    borderBottom: "1px solid ".concat(theme.palette.gray[200])
  } : {};
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      width: '100%'
    },
    "data-element": "Box",
    "data-component": "HorizontalTabs",
    "data-source-file": "HorizontalTabs.tsx",
    children: isMobile ? /*#__PURE__*/_jsxs(Stack, {
      className: "horizontal-tabs-container",
      spacing: 2,
      children: [/*#__PURE__*/_jsx(InputDropdown, _extends({
        helpIcon: false,
        menuItems: dropdownItems,
        onChange: handleTabChange,
        size: size,
        value: ((_selectedTab$tab = selectedTab.tab) == null ? void 0 : _selectedTab$tab.key) || tabs[0].key
      }, mobileDropdownProps)), children]
    }) : /*#__PURE__*/_jsxs(Stack, {
      alignItems: "end",
      className: "horizontal-tabs-container",
      direction: "row",
      children: [/*#__PURE__*/_jsx(MuiTabs, _extends({
        ref: tabsRef
      }, otherTabsProps, {
        ScrollButtonComponent: function ScrollButtonComponent(button) {
          var direction = button.direction,
            disabled = button.disabled,
            others = _objectWithoutProperties(button, _excluded2);
          if (disabled) return null;
          var isLeft = direction === 'left';
          return /*#__PURE__*/_jsx(Box, {
            sx: _extends({
              alignItems: 'center',
              display: 'flex',
              pl: isLeft ? 0 : '8px',
              pr: isLeft ? '8px' : 0
            }, (type === 'buttonWhiteBorder' || type === 'buttonPrimaryBorder') && _extends({}, isLeft ? {
              borderLeft: bwBorder,
              borderRadius: "".concat(bwBorderRadius, " 0 0 ").concat(bwBorderRadius),
              mr: '-6px',
              p: "".concat(bwPadding, " 8px ").concat(bwPadding, " ").concat(bwPadding)
            } : {
              borderRadius: "0 ".concat(bwBorderRadius, " ").concat(bwBorderRadius, " 0"),
              borderRight: bwBorder,
              ml: '-6px',
              p: "".concat(bwPadding, " ").concat(bwPadding, " ").concat(bwPadding, " 8px")
            }, {
              backgroundColor: theme.palette.gray[50],
              borderBottom: bwBorder,
              borderTop: bwBorder,
              zIndex: 5
            }), underlineBorder),
            children: /*#__PURE__*/_jsx(Button, _extends({
              size: size,
              sx: {
                padding: '8px'
              },
              variant: "secondary-gray"
            }, others, {
              children: isLeft ? /*#__PURE__*/_jsx(Icon, {
                color: theme.palette.gray[700],
                iconId: "ChevronLeft",
                size: 20
              }) : /*#__PURE__*/_jsx(Icon, {
                color: theme.palette.gray[700],
                iconId: "ChevronRight",
                size: 20
              })
            }))
          });
        },
        TabIndicatorProps: {
          style: {
            display: 'none'
          }
        },
        allowScrollButtonsMobile: true,
        scrollButtons: "auto",
        sx: {
          '&.MuiTabs-root': {
            minHeight: 0
          },
          '.MuiTab-root': {
            minWidth: 'none',
            width: fullWidth ? "".concat(1 / tabs.length * 100, "%") : 'auto'
          },
          '.MuiTabs-flexContainer': _extends({}, fullWidth && {
            flex: 1
          }, type === 'buttonWhiteBorder' && {
            pl: '4px',
            pr: '4px'
          }),
          '.MuiTabs-scroller': _extends({}, (type === 'buttonWhiteBorder' || type === 'buttonPrimaryBorder') && {
            backgroundColor: theme.palette.gray[50],
            border: bwBorder,
            borderRadius: bwBorderRadius,
            pb: bwPadding,
            pt: bwPadding
          }, underlineBorder, (type === 'buttonGray' || type === 'buttonPrimary' || type === 'buttonPrimaryBorder') && {
            p: '4px'
          }),
          width: '100%'
        },
        value: selectedTab.index || 0,
        variant: fullWidth ? 'fullWidth' : 'scrollable',
        children: tabButtons
      })), children && /*#__PURE__*/_jsx(Stack, {
        sx: {
          alignItems: 'flex-end',
          border: 'none',
          borderBottom: (_underlineBorder$bord = underlineBorder == null ? void 0 : underlineBorder.borderBottom) != null ? _underlineBorder$bord : 'none',
          direction: 'row',
          display: 'flex',
          flexGrow: 1,
          height: '200px',
          justifyContent: 'center',
          maxHeight: tabsHeight,
          pl: 4
        },
        children: children
      })]
    })
  });
}